import { Component } from "@theme/component";

// Utilidades
const MESES = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

/** @param {number} monthIndex 0-11 */
function getMesExtenso(monthIndex) {
  return MESES[monthIndex] ?? "";
}

/**
 * @param {Date} data
 * @param {number} dias
 */
function adicionarDiasUteis(data, dias) {
  const d = new Date(data);
  let contador = 0;
  while (contador < dias) {
    d.setDate(d.getDate() + 1);
    const diaSemana = d.getDay();
    if (diaSemana !== 0 && diaSemana !== 6) contador++;
  }
  return d;
}

/** @param {string} uf */
function getDeliveryEstimate(uf) {
  const today = new Date();

  const prazoMap = {
    Norte: { min: 5, max: 15 },
    Nordeste: { min: 4, max: 12 },
    "Sul, Sudeste, Centro": { min: 3, max: 8 },
  };

  // Região por UF
  /** @type {'Norte' | 'Nordeste' | 'Sul, Sudeste, Centro'} */
  let region = "Sul, Sudeste, Centro";
  if (["AM", "AC", "RR", "RO", "PA", "AP", "TO"].includes(uf)) region = "Norte";
  else if (["BA", "PE", "CE", "RN", "PB", "SE", "AL", "MA", "PI"].includes(uf))
    region = "Nordeste";

  const { min, max } = prazoMap[region];
  const startDate = adicionarDiasUteis(today, min);
  const endDate = adicionarDiasUteis(today, max);

  const startMonth = getMesExtenso(startDate.getMonth());
  const endMonth = getMesExtenso(endDate.getMonth());

  if (startMonth === endMonth) {
    return `${startDate.getDate()} a ${endDate.getDate()} de ${startMonth}`;
  }

  const startDateFormatted = `${String(startDate.getDate()).padStart(
    2,
    "0"
  )}/${String(startDate.getMonth() + 1).padStart(2, "0")}`;
  return `${startDateFormatted} a ${endDate.getDate()} de ${endMonth}`;
}

/** @param {string} cep */
function sanitizeCep(cep) {
  return String(cep).replace(/\D/g, "");
}

/** @param {string} cep */
function isValidCep(cep) {
  return /^\d{8}$/.test(sanitizeCep(cep));
}

const REGION_PRICE = {
  Norte: "R$ 17,90",
  Nordeste: "R$ 14,90",
  "Sul, Sudeste, Centro": "R$ 11,90",
};

/** @param {string} uf */
function getRegionPrice(uf) {
  if (["AM", "AC", "RR", "RO", "PA", "AP", "TO"].includes(uf))
    return REGION_PRICE.Norte;
  if (["BA", "PE", "CE", "RN", "PB", "SE", "AL", "MA", "PI"].includes(uf))
    return REGION_PRICE.Nordeste;
  return REGION_PRICE["Sul, Sudeste, Centro"];
}

/**
 * Web Component: <calculo-frete>
 * Integra com o bloco Liquid e calcula frete via ViaCEP.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} cep
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} result
 * @property {HTMLElement} resultTitle
 * @property {HTMLElement} resultSub
 *
 * @extends Component<Refs>
 */
class CalculoFrete extends Component {
  requiredRefs = ["cep", "button", "result", "resultTitle", "resultSub"];

  connectedCallback() {
    super.connectedCallback();

    this.refs.cep.addEventListener("input", this.#maskCep);
    this.refs.cep.addEventListener("keydown", this.#onKeydown);
    this.refs.button.addEventListener("click", this.#handleCalc);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.cep?.removeEventListener("input", this.#maskCep);
    this.refs.cep?.removeEventListener("keydown", this.#onKeydown);
    this.refs.button?.removeEventListener("click", this.#handleCalc);
  }

  /** @param {KeyboardEvent} e */
  #onKeydown = (e) => {
    if (!(e instanceof KeyboardEvent)) return;
    if (e.key !== "Enter") return;
    e.preventDefault();
    this.#handleCalc();
  };

  #maskCep = () => {
    const digits = sanitizeCep(this.refs.cep.value).slice(0, 8);

    if (digits.length <= 5) {
      this.refs.cep.value = digits;
      return;
    }

    this.refs.cep.value = `${digits.slice(0, 5)}-${digits.slice(5)}`;
  };

  /** @param {boolean} loading */
  #setLoading(loading) {
    this.refs.button.disabled = loading;
    this.refs.button.ariaBusy = String(loading);

    if (loading) {
      this.refs.button.dataset.originalText =
        this.refs.button.textContent || "";
      this.refs.button.textContent = "Calculando…";
      this.#showMessage("Consultando CEP…", "");
      return;
    }

    this.refs.button.textContent =
      this.refs.button.dataset.originalText ||
      this.refs.button.textContent ||
      "Calcular";
  }

  /**
   * @param {string} title
   * @param {string} subtext
   */
  #showMessage(title, subtext) {
    this.refs.resultTitle.textContent = title;
    this.refs.resultSub.textContent = subtext;
    this.refs.result.hidden = false;
  }

  #handleCalc = async () => {
    const cep = sanitizeCep(this.refs.cep.value);

    if (!isValidCep(cep)) {
      this.#showMessage("CEP inválido. Insira no formato 00000-000.", "");
      return;
    }

    try {
      this.#setLoading(true);
      await new Promise((r) => setTimeout(r, 200));

      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("Falha na consulta do CEP");

      const data = await res.json();

      if (data?.erro) {
        this.#showMessage("CEP não encontrado. Tente novamente.", "");
        return;
      }

      const uf = String(data.uf || "").toUpperCase();
      const localidade = data.localidade || "";

      const price = getRegionPrice(uf);
      const estimate = getDeliveryEstimate(uf);

      const placeText =
        localidade && uf ? `${localidade} - ${uf}` : uf || localidade || "";

      this.#showMessage(
        placeText
          ? `Frete Fixo por ${price} para ${placeText}`
          : `Frete Fixo por ${price}`,
        `Entrega estimada entre ${estimate}.`
      );
    } catch (err) {
      this.#showMessage(
        "Erro ao buscar informações do CEP. Tente novamente.",
        ""
      );
    } finally {
      this.#setLoading(false);
    }
  };
}

if (!customElements.get("calculo-frete")) {
  customElements.define("calculo-frete", CalculoFrete);
}
